// ADR-0006 external delivery: the provider serves this title only over HTTP, which an HTTPS page cannot load (mixed
// content). Offer external players and the link itself. The URL is the provider's short-lived, credential-free token
// URL (POST /api/playback refuses anything carrying credentials). No in-app playback, no progress tracking.
import { useId, useState } from 'react';

/**
 * Deep links, per platform (documented schemes):
 * - VLC for iOS/iPadOS: `vlc-x-callback://x-callback-url/stream?url=…` (VideoLAN wiki, Documentation:IOS)
 * - VLC for Android: a Chrome `intent:` URL for package org.videolan.vlc (plays the http(s) link it wraps)
 * - desktop VLC registers no URL handler, so `vlc://…` works only where a handler exists; the panel says to copy the link
 * - Infuse (Apple platforms only): `infuse://x-callback-url/play?url=…` (Firecore x-callback-url)
 */
export function externalLinks(url: string, ua: string, touch: boolean): { vlc: string; infuse: string | null; desktop: boolean } {
  const enc = encodeURIComponent(url);
  const ios = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && touch); // iPadOS reports a Mac user agent
  const android = /Android/.test(ua);
  const apple = ios || /Macintosh/.test(ua);
  const vlc = ios
    ? `vlc-x-callback://x-callback-url/stream?url=${enc}`
    : android
      ? `intent:${url.replace(/^[a-z]+:/i, '')}#Intent;scheme=${new URL(url).protocol.slice(0, -1)};type=video/*;package=org.videolan.vlc;end`
      : `vlc://${url}`;
  return { vlc, infuse: apple ? `infuse://x-callback-url/play?url=${enc}` : null, desktop: !ios && !android };
}

export function ExternalPanel({ url, onClose }: { url: string; onClose(): void }) {
  const [copy, setCopy] = useState<'idle' | 'copied' | 'failed'>('idle');
  const titleId = useId();
  const links = externalLinks(url, navigator.userAgent, (navigator.maxTouchPoints ?? 0) > 1);
  const copyLink = () => {
    const p = navigator.clipboard?.writeText(url);
    if (!p) { setCopy('failed'); return; }
    p.then(() => setCopy('copied'), () => setCopy('failed'));
  };
  return (
    <div className="tp-error" role="group" aria-labelledby={titleId} data-testid="player-external">
      <p className="tp-error-title" id={titleId}>Open in another player</p>
      <p>This title is served over an unencrypted link your browser won't play inside Tuned.</p>
      {links.desktop && <p className="tp-muted">On a computer, copy the link and open it in VLC with Media › Open Network Stream.</p>}
      <div className="tp-error-actions">
        <a className="tp-btn tp-btn-primary" href={links.vlc}>Open in VLC</a>
        {links.infuse && <a className="tp-btn" href={links.infuse}>Open in Infuse</a>}
        <button type="button" className="tp-btn" onClick={copyLink}>Copy link</button>
        <button type="button" className="tp-btn" onClick={onClose}>Close</button>
      </div>
      <p className="tp-muted" role="status">
        {copy === 'copied' ? 'Link copied. It stops working after a while; come back here for a new one.' : copy === 'failed' ? 'Copying was blocked. Select the link below and copy it.' : ''}
      </p>
      {copy === 'failed' && <input className="tp-link" readOnly value={url} aria-label="Stream link" onFocus={(e) => e.currentTarget.select()} />}
    </div>
  );
}
